import {Requestor, TokenResponse} from '@openid/appauth';
import {HttpErrorResponse} from '@angular/common/http';
import {NavController, Platform} from '@ionic/angular';
import {Injectable, NgZone, OnDestroy} from '@angular/core';
import {AuthActions, Browser, IAuthAction, IAuthConfig} from 'ionic-appauth/lib/';
import {environment} from '../../environments/environment';
import {LocalStorageService} from '../services/local-storage/local-storage.service';
import {defer, from, Observable, of, Subject, Subscription} from 'rxjs';
import {isDefined} from '../commons/utils';
import {NgHttpService} from './ng-http.service';
import {PrincipalService} from '../services/principal/principal.service';
import {SharedDataService} from '../services/shared-data/shared-data.service';
import {AppConstants} from '../commons/app-constants';
import {SecuredToken} from '../domain/secured-token';
import {EtEncryptService} from '../services/et-encrypt/et-encrypt.service';
import {catchError, filter, finalize, map, shareReplay, tap} from 'rxjs/operators';
import {User} from '../domain/user';
import {UserDeviceService} from '../services/user-device/user-device.service';
import {AppPaths} from '../commons/app-paths';
import {LoadingSpinnerService} from '../services/loading-service/loading-spinner.service';
import {FirebaseService} from '../services/firebase/firebase.service';
import {CustomIonicAppauth} from './custom-ionic-appauth';
import {App} from '@capacitor/app';

@Injectable({
  providedIn: 'root'
})
export class AuthorizationService implements OnDestroy {
  private readonly isAuthenticatedState = new Subject<boolean>();
  private readonly tokenUpdate = new Subject<TokenResponse>();
  private readonly subscription: Subscription;
  public authService: CustomIonicAppauth;
  private static readonly REFRESH_LEAD_FACTOR = 0.8;
  private storageAvailable: boolean;
  private refreshInFlight$: Observable<TokenResponse> | null = null;
  private proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly requestor: Requestor,
              browser: Browser,
              private readonly platform: Platform,
              private readonly ngZone: NgZone,
              private readonly localStorage: LocalStorageService,
              private readonly principal: PrincipalService,
              private readonly sharedDataService: SharedDataService,
              private readonly etEncryptService: EtEncryptService,
              private readonly userDeviceService: UserDeviceService,
              private readonly navCtrl: NavController,
              private readonly loadingSpinnerService: LoadingSpinnerService) {
    this.authService = new CustomIonicAppauth(etEncryptService, navCtrl, requestor, browser, localStorage);
    this.subscription = this.getAuthService()
      .events$.subscribe(action => this.postCallback(action));
  }

  public async startUpAsync(): Promise<void> {
    await this.addConfig();

    if (this.platform.is('hybrid')) {
      App.addListener('appUrlOpen', data => {
        this.handleCallback(data.url);
      });
    }

    this.platform.pause.subscribe(() => this.cancelProactiveRefresh());
    this.platform.resume.subscribe(() => this.refreshActiveSession());
  }

  private onDevice(): boolean {
    return this.platform.is('hybrid');
  }

  getAuthService(): CustomIonicAppauth {
    return this.authService;
  }

  getIssuer(): string {
    return this.getAuthService().authConfig.server_host;
  }

  getAuthConfig(): IAuthConfig {
    return this.getAuthService().authConfig;
  }

  isAuthenticated(): Observable<boolean> {
    return this.getAuthService().isAuthenticated$;
  }

  private async addConfig(): Promise<any> {
    const redirectUri = this.onDevice() ? `${FirebaseService.getAppLink()}:/callback` : window.location.origin + '/callback';
    const logoutRedirectUri = this.onDevice() ? `${FirebaseService.getAppLink()}:/logout` : window.location.origin + '/logout';
    const clientId = environment.iamClientID;
    const issuer = FirebaseService.getIssuer();
    this.getAuthService().authConfig = {
      client_id: clientId,
      server_host: issuer,
      redirect_url: redirectUri,
      end_session_redirect_url: logoutRedirectUri,
      scopes: 'openid',
      pkce: true
    };
    this.getAuthService().setupAuthorizationNotifier();
  }

  private handleCallback(callbackUrl: string): void {
    if ((callbackUrl).startsWith(this.getAuthService().authConfig.redirect_url)) {
      this.storageAvailable = this.localStorage.isStorageAvailable();
      if (this.storageAvailable) {
        this.loadingSpinnerService.showLoadingDialogForKeycloak();
        this.getAuthService().authorizationCallback(callbackUrl);
      }
    }

    if ((callbackUrl).startsWith(this.getAuthService().authConfig.end_session_redirect_url)) {
      this.loadingSpinnerService.showLoadingDialogForKeycloak();
      this.getAuthService().endSessionCallback();
    }
  }

  private postCallback(action: IAuthAction): void {
    if (action.action === AuthActions.SignInSuccess) {
      this.navCtrl.navigateRoot(AppPaths.DEFAULT_PAGE)
        .then(() => this.loadingSpinnerService.hideLoadingDialogForKeycloak());
    }

    if (action.action === AuthActions.SignOutSuccess) {
      this.removeAuthData()
        .then(() => this.navCtrl.navigateRoot(AppPaths.DEFAULT_PAGE)
          .then(() => this.loadingSpinnerService.hideLoadingDialogForKeycloak()));
    }
  }

  refreshToken(pinCode: string, newPinCode?: string): Observable<TokenResponse> {
    if (isDefined(newPinCode)) {
      return this.performRefresh(pinCode, newPinCode);
    }
    return this.refreshInFlight$ ?? this.startSharedRefresh(pinCode);
  }

  private startSharedRefresh(pinCode: string): Observable<TokenResponse> {
    const refresh$ = this.performRefresh(pinCode).pipe(
      finalize(() => this.refreshInFlight$ = null),
      shareReplay({bufferSize: 1, refCount: false})
    );
    this.refreshInFlight$ = refresh$;
    return refresh$;
  }

  isTerminalRefreshError(): boolean {
    const error = this.getRequestorTokenError();
    if (!error) {
      return false;
    }
    if (error.status === 0 || error.status === 419 || error.status >= 500) {
      return false;
    }
    return true;
  }

  clearAuthStorage(): void {
    if (isDefined(this.localStorage.getItem(AppConstants.TOKEN_RESPONSE))) {
      this.localStorage.removeItem(AppConstants.TOKEN_RESPONSE);
    }
  }

  private performRefresh(pinCode: string, newPinCode?: string): Observable<TokenResponse> {
    return defer(() => {
      this.resetRequestorTokenError();
      return from(
        this.getAuthService().internalLoadTokenFromStorageWithPin(pinCode)
          .then(() => this.doRefreshToken(pinCode, newPinCode))
      );
    });
  }

  private doRefreshToken(pinCode: string, newPinCode?: string): Promise<TokenResponse> {
    return this.getAuthService().getValidToken().then(response => {
      this.clearAuthStorage();
      const newToken: TokenResponse = response;
      if (isDefined(newPinCode)) {
        this.sharedDataService.setSharedData<string>(AppConstants.USER_PIN_CODE, newPinCode);
        this.saveToken(newToken, newPinCode);
      } else {
        this.sharedDataService.setSharedData<string>(AppConstants.USER_PIN_CODE, pinCode);
        this.saveToken(newToken, pinCode);
      }
      this.tokenUpdate.next(newToken);
      this.scheduleProactiveRefresh(newToken);
      return newToken;
    });
  }

  private scheduleProactiveRefresh(token: TokenResponse): void {
    this.cancelProactiveRefresh();
    const lifetime = token?.expiresIn;
    if (!lifetime || lifetime <= 0) {
      return;
    }
    const delayMs = Math.floor(lifetime * AuthorizationService.REFRESH_LEAD_FACTOR * 1000);
    this.proactiveRefreshTimer = setTimeout(() => {
      this.proactiveRefreshTimer = null;
      this.refreshActiveSession();
    }, delayMs);
  }

  private cancelProactiveRefresh(): void {
    if (isDefined(this.proactiveRefreshTimer)) {
      clearTimeout(this.proactiveRefreshTimer);
      this.proactiveRefreshTimer = null;
    }
  }

  private refreshActiveSession(): void {
    if (this.sharedDataService.getSharedData<boolean>(AppConstants.REQUIRES_PIN)) {
      return;
    }
    const pin = this.sharedDataService.getSharedData<string>(AppConstants.USER_PIN_CODE);
    if (isDefined(pin) && isDefined(this.localStorage.getItem(AppConstants.SECURED_TOKEN))) {
      this.refreshToken(pin).subscribe({error: () => undefined});
    }
  }

  private resetRequestorTokenError(): void {
    if (this.requestor instanceof NgHttpService) {
      this.requestor.resetLastTokenError();
    }
  }

  private getRequestorTokenError(): HttpErrorResponse | null {
    return this.requestor instanceof NgHttpService ? this.requestor.getLastTokenError() : null;
  }

  private saveToken(token: TokenResponse, pinCode: string): void {
    this.sharedDataService.setSharedData<string>(AppConstants.ACCESS_TOKEN, token.accessToken);
    this.localStorage.setItem(AppConstants.SECURED_TOKEN,
      this.etEncryptService.encryptTokenSignature(token, pinCode));
  }

  isNotExpiredToken(): boolean {
    const token = this.localStorage.getItem<SecuredToken>(AppConstants.SECURED_TOKEN);
    return isDefined(token) && token.issuedAt + token.expiresIn > Math.round(Date.now() / 1000);
  }

  checkRefreshToken(): Observable<boolean> {
    const pin = this.sharedDataService.getSharedData<string>(AppConstants.USER_PIN_CODE);
    if (isDefined(pin)) {
      return this.isNotExpiredToken()
        ? of(true)
        : this.refreshToken(pin).pipe(
            map(() => true),
            catchError(() => of(false))
          );
    }
    return of(false);
  }

  removeAuthData(): Promise<void> {
    this.cancelProactiveRefresh();
    this.refreshInFlight$ = null;
    return this.localStorage.clear()
      .catch(() => undefined)
      .then(() => {
        this.sharedDataService.clear();
        this.isAuthenticatedState.next(false);
        this.principal.authenticate(null);
      });
  }

  onTokenUpdate(): Observable<TokenResponse> {
    return this.tokenUpdate.asObservable();
  }

  onAuthenticate(): Observable<boolean> {
    return this.isAuthenticatedState.asObservable();
  }

  applyPostLoginActions(): Observable<User> {
    return this.principal.identity(true).pipe(
      filter(account => isDefined(account)),
      tap(user => {
        this.userDeviceService.register(user.userId).subscribe();
        this.isAuthenticatedState.next(true);
      })
    );
  }

  authorize(): void {
    this.getAuthService().signIn().then();
  }

  logout(): void {
    this.cancelProactiveRefresh();
    this.refreshInFlight$ = null;
    this.getAuthService().signOut().then();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
